import { Switch } from '@startupquickstart/frontend';

import {
  EVENT_REALTIME_SETTINGS,
  useEventSettingMutation,
  type EventRealtimeSetting,
} from '@/components/events/useEventSettingMutation';
import type { Event } from '@/config/objects/events';
import { cn } from '@/lib/utils';

const TOGGLE_ORDER: readonly EventRealtimeSetting[] = [
  'leaderboardVisible',
  'teamChatEnabled',
  'helpChatEnabled',
  'quizMode',
  'aiValidationEnabled',
  'requireMediaReview',
  'enforceTimeLimits',
  'requireSingleDevice',
  'requireTeamTogether',
];

function SettingToggle({ event, setting }: { event: Event; setting: EventRealtimeSetting }) {
  const config = EVENT_REALTIME_SETTINGS[setting];
  const mutation = useEventSettingMutation(event.id, setting);
  const checked = (event[setting] as boolean | undefined) ?? config.defaultValue;

  return (
    <div>
      <p className="text-sm text-muted-foreground mb-1">{config.label}</p>
      <div
        role="button"
        className="flex items-center gap-2 cursor-pointer disabled:opacity-50"
        onClick={() => mutation.mutate(!checked)}
      >
        <Switch checked={checked} disabled={mutation.isPending} tabIndex={-1} />
        <span className="text-sm font-medium">{checked ? config.onLabel : config.offLabel}</span>
      </div>
    </div>
  );
}

export interface EventRealtimeTogglesProps {
  event: Event;
  className?: string;
}

/**
 * The four live-tunable event switches (leaderboard visibility, team chat,
 * help chat, quiz mode) with optimistic updates. Rendered inside the
 * Overview info grid and reused as a panel on the Live tab.
 *
 * @param props - Event whose settings to control plus a layout class.
 */
export function EventRealtimeToggles({ event, className }: EventRealtimeTogglesProps) {
  return (
    <div className={cn('grid gap-6 sm:grid-cols-2', className)}>
      {TOGGLE_ORDER.map(setting => (
        <SettingToggle key={setting} event={event} setting={setting} />
      ))}
    </div>
  );
}
